import React, { useState, useEffect } from 'react';
import { useFormContext, Controller, get, useWatch, Path } from 'react-hook-form';
import { Search, Paperclip, X, Trash2 } from 'lucide-react';

import { OrderSchemaType, ItemPath } from '../../../order.schema';
import { useAttachmentHandler } from '../../../Hooks/useAttachmentHandler';
import { useApiData } from '../../../contexts/ApiDataContext';
import { FINAL_PRODUCT_FORM_DATA } from '../../../constants';
import SteelOrdersInfiniteSelect from '../../../components/SteelOrdersInfiniteSelect';
import { NumericInput } from '../../../components/NumericInput';

import {
  FormGrid,
  FormGroup,
  Input,
  Label,
  Select,
  AttachmentList,
  AttachmentListItem,
  RemoveAttachmentButton,
  FormErrorMessage,
  AttachmentInputContainer,
  AttachmentTrigger,
  AttachmentPlaceholder,
  TrashIconContainer,
} from '../../../style';

const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders`;

interface FinalProductSectionProps {
  itemPath: ItemPath;
}

const FinalProductSection: React.FC<FinalProductSectionProps> = ({ itemPath }) => {
  const { register, control, formState: { errors }, setValue, watch } = useFormContext<OrderSchemaType>();
  const { scripts } = useApiData();
  const [selectedScript, setSelectedScript] = useState<{ id: number; name: string }[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<{ id: number; name: string }[]>([]);
  


  const [scriptOptions, setScriptOptions] = useState<{ id: number; name: string }[]>([]);

  const {
    attachments,
    attachmentError,
    fileInputRef,
    handleFileSelectClick,
    handleFileChange,
    handleRemoveAttachment,
    handleRemoveAllAttachments,
  } = useAttachmentHandler({ fieldPath: `${itemPath}.finalProduct.anexos` });

  const finalProductPath = `${itemPath}.finalProduct` as const;

  useEffect(() => {
    if (selectedScript.length > 0) {
      setValue(`${finalProductPath}.script`, selectedScript[0].name);
      setValue((`${itemPath}.scriptId`) as any, selectedScript[0].id, { shouldDirty: true, shouldValidate: false });
    }
  }, [selectedScript, setValue, finalProductPath]);

  useEffect(() => {
    const existingProductId = watch((`${itemPath}.productId`) as any) as number | undefined;
    const existingScriptId = watch((`${itemPath}.scriptId`) as any) as number | undefined;
    if (existingProductId && selectedProduct.length === 0) {
      setSelectedProduct([{ id: existingProductId, name: watch(`${finalProductPath}.produto`) || '' }]);
    }
    if (existingScriptId && selectedScript.length === 0) {
      setSelectedScript([{ id: existingScriptId, name: watch(`${finalProductPath}.script`) || '' }]);
    }
  }, [watch, itemPath, finalProductPath, selectedProduct.length, selectedScript.length]);

  useEffect(() => {
    if (selectedProduct.length > 0) {
      setValue(`${finalProductPath}.produto`, selectedProduct[0].name);
      setValue((`${itemPath}.productId`) as any, selectedProduct[0].id, { shouldDirty: true, shouldValidate: false });
      setSelectedScript([]);
      setValue(`${finalProductPath}.script`, '');
		} else {
			const existingProductId = watch((`${itemPath}.productId`) as any) as number | undefined;
			if (existingProductId === undefined || existingProductId === null) {
				setValue((`${itemPath}.productId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
			}
			setSelectedScript([]);
			setValue(`${finalProductPath}.script`, '');
    }
	}, [selectedProduct, setValue, finalProductPath, itemPath, watch]);

  useEffect(() => {
    const qty = Number(watch(`${finalProductPath}.quantidade` as any));
    if (!isNaN(qty)) {
      setValue((`${itemPath}.quantity`) as any, qty, { shouldDirty: true, shouldValidate: false });
    }
  }, [watch(`${finalProductPath}.quantidade` as any)]);

  const watchedProductId = useWatch({ control, name: (`${itemPath}.productId` as any) });
  const watchedProductName = useWatch({ control, name: (`${finalProductPath}.produto` as any) });
  const watchedScriptId = useWatch({ control, name: (`${itemPath}.scriptId` as any) });
  const watchedScriptName = useWatch({ control, name: (`${finalProductPath}.script` as any) });

  useEffect(() => {
    if (watchedProductId && selectedProduct.length === 0) {
      setSelectedProduct([{ id: Number(watchedProductId), name: String(watchedProductName || '') }]);
      
    }
    if (watchedScriptId && selectedScript.length === 0) {
      setSelectedScript([{ id: Number(watchedScriptId), name: String(watchedScriptName || '') }]);
      
    }
  }, [watchedProductId, watchedProductName, watchedScriptId, watchedScriptName, selectedProduct.length, selectedScript.length]);

  useEffect(() => {
    const fetchScriptName = async () => {
      if (watchedScriptId && (!watchedScriptName || String(watchedScriptName).trim() === '')) {
        try {
          const res = await fetch(`${API_BASE_URL}/select-in-order/scripts?id=${Number(watchedScriptId)}`);
          const data = await res.json();
          const scripts = Array.isArray(data?.scripts) ? data.scripts : Array.isArray(data) ? data : [];
          const first = scripts[0];
          const name = typeof first === 'string' ? first : (first?.name ?? '');
          if (name) {
            setValue((`${finalProductPath}.script`) as any, name, { shouldDirty: false, shouldValidate: false });
            setSelectedScript([{ id: Number(watchedScriptId), name }]);
            
          }
        } catch (e) {
          void e;
        }
      }
    };
    fetchScriptName();
  }, [watchedScriptId, watchedScriptName, setValue, finalProductPath]);

  return (
    <>
      <FormGrid columns={3} style={{ marginTop: '16px' }}>
          <SteelOrdersInfiniteSelect
            label="Produto"
            items={selectedProduct}
            setItems={setSelectedProduct}
            endpoint="products"
            placeholder="Procure por nome ou código"
            onCleared={() => {
              setValue((`${itemPath}.productId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
              setValue((`${finalProductPath}.produto`) as any, '', { shouldDirty: true, shouldValidate: false });
              setSelectedScript([]);
              setValue(`${finalProductPath}.script`, '');
            }}
          />
        <FormGroup>
          <Label htmlFor={`${finalProductPath}.tipoProduto`}>Tipo Produto</Label>
          <Controller
            name={`${finalProductPath}.tipoProduto`}
            control={control}
            render={({ field }) => (
              <Select id={`${finalProductPath}.tipoProduto`} {...field} hasError={!!get(errors, `${finalProductPath}.tipoProduto`)}>
                <option value="">Selecione</option>
                {FINAL_PRODUCT_FORM_DATA.productTypes.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </Select>
            )}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor={`${finalProductPath}.fabricante`}>Fabricante</Label>
          <Controller
            name={`${finalProductPath}.fabricante`}
            control={control}
            render={({ field }) => (
              <Select id={`${finalProductPath}.fabricante`} {...field} hasError={!!get(errors, `${finalProductPath}.fabricante`)}>
                <option value="">Selecione</option>
                {FINAL_PRODUCT_FORM_DATA.manufacturers.map(type => <option key={type} value={type}>{type}</option>)}
              </Select>
            )}
          />
        </FormGroup>
      </FormGrid>

      <FormGrid columns={3} style={{ marginTop: '16px' }}>
        <SteelOrdersInfiniteSelect
          label="Script"
          items={selectedScript}
          setItems={setSelectedScript}
          endpoint="scripts"
          placeholder="Selecione o script"
          additionalParams={selectedProduct[0]?.id ? { productId: selectedProduct[0].id } : {}}
          key={selectedProduct[0]?.id || 'product-scripts'}
          searchPlaceholder={selectedProduct[0]?.id ? 'Nenhum script encontrado' : 'Selecione um produto primeiro'}
          isDisabled={!selectedProduct[0]?.id}
          onCleared={() => {
            setValue((`${itemPath}.scriptId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
            setValue((`${finalProductPath}.script`) as any, '', { shouldDirty: true, shouldValidate: false });
          }}
        />
        <FormGroup>
          <Label htmlFor={`${finalProductPath}.quantidade`}>Quantidade</Label>
          <NumericInput
            id={`${finalProductPath}.quantidade`}
            name={`${finalProductPath}.quantidade`}
            value={(watch(`${finalProductPath}.quantidade` as any) as any) || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(`${finalProductPath}.quantidade` as any, e.target.value, { shouldDirty: true })}
            onBlur={() => {}}
            hasError={!!get(errors, `${finalProductPath}.quantidade`)}
          />
        </FormGroup>
        <FormGroup>
          <Label>Arquivo</Label>
          <AttachmentInputContainer>
            <AttachmentTrigger onClick={handleFileSelectClick} hasError={!!attachmentError}>
              <Paperclip size={16} />
              <AttachmentPlaceholder>Clique para anexar arquivos</AttachmentPlaceholder>
            </AttachmentTrigger>
            {attachments.length > 0 && (
              <TrashIconContainer
                as="button"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemoveAllAttachments();
                }}
              >
                <Trash2 size={16} />
              </TrashIconContainer>
            )}
          </AttachmentInputContainer>
          {attachments.length > 0 && (
            <AttachmentList>
              {attachments.map((file, index) => (
                <AttachmentListItem key={index}>
                  {file.name}
                  <RemoveAttachmentButton
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveAttachment(index);
                    }}
                  >
                    <X size={12} />
                  </RemoveAttachmentButton>
                </AttachmentListItem>
              ))}
            </AttachmentList>
          )}
          <input
            type="file"
            multiple
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            accept=".pdf,.xml,.doc,.docx,.xls,.xlsx,.jpeg,.jpg,.png"
          />
          {attachmentError && <FormErrorMessage>{attachmentError}</FormErrorMessage>}
        </FormGroup>
      </FormGrid>
    </>
  );
};

export default FinalProductSection;
